import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore, SliceProps } from '../../store';
import {
    urlReportsExcelReport,
    urlReportsExcelTemplates,
    urlReportsRedliningReport,
} from '../../../Constants';
import { IReportTemplate } from '../../../types/reportTemplate';
import { downloadDocumentsStream } from '../../../util/FileUtils';
import { sortTemplates } from '../../../util/TemplateUtils';
import { LoadingState } from '../../../types';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Standardizes and sorts an array of report templates.
 *
 * For each template, if the `DocumentChoice_DocType` property is empty,
 * it is replaced with the provided default `docTypes`.
 * The resulting array is then sorted using the `sortTemplates` function.
 *
 * @param templates The array of report templates to standardize and sort.
 * @param docTypes The default document types to use when `DocumentChoice_DocType` is empty.
 * @returns The standardized and sorted array of report templates.
 */
const standardizeAndSortTemplates = (
    templates: IReportTemplate[],
    docTypes: string[],
): IReportTemplate[] => {
    return sortTemplates(templates).map(item => ({
        ...item,
        DocumentChoice_DocType: item.DocumentChoice_DocType.length
            ? item.DocumentChoice_DocType
            : docTypes,
    }));
};

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsReportFromTemplateState = {
    isDialogOpen: false,
    isReportCreated: false,
    loading: false,
    error: null,
    docTypes: null,
    templates: null,
    selectedTemplate: null,
    downloadedReports: 0,
    redliningReportState: null,
    typeOfDialog: null,
};

const reducers = {
    openCreateReportFromTemplateDialog(
        state: IProjectDocumentsReportFromTemplateState,
        action: IAction<CreateReportFromTemplateDialogEnum>,
    ) {
        state.isDialogOpen = true;
        state.typeOfDialog = action.payload;
    },
    closeCreateReportFromTemplateDialog(state: IProjectDocumentsReportFromTemplateState) {
        state.isDialogOpen = false;
        state.isReportCreated = false;
        state.typeOfDialog = null;
    },
    selectReportTemplate(
        state: IProjectDocumentsReportFromTemplateState,
        action: IAction<IReportTemplate>,
    ) {
        state.selectedTemplate = action.payload;
    },
    resetAfterDownloadingReports(state: IProjectDocumentsReportFromTemplateState) {
        state.downloadedReports = 0;
        state.isReportCreated = false;
    },
    resetDownloadedReportsErrorState(state: IProjectDocumentsReportFromTemplateState) {
        state.error = null;
    },
    resetRedliningReportState(state: IProjectDocumentsReportFromTemplateState) {
        state.redliningReportState = null;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get email templates for Upload Additional Documents Dialog
     */
    getReportTemplatesForCreateReport: async (config: {
        replicaId: string;
        typeOfDialog: CreateReportFromTemplateDialogEnum;
    }): Promise<IGetReportTemplatesResponse> => {
        const { replicaId, typeOfDialog } = config;
        try {
            const request: RequestInit = {
                headers: {
                    replicaId,
                    templateSelection: typeOfDialog,
                },
            };
            const response = await apiFetch(urlReportsExcelTemplates, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onConfirmCreateReport: async (
        config: {
            selectedDocumentTypes: string[];
            selectedVersionToInclude: SelectReportVersionEnum;
            fileName: string;
            replicaId: string;
            sendTo?: string[];
        },
        thunkApi,
    ): Promise<number> => {
        try {
            const { replicaId, fileName, selectedVersionToInclude, selectedDocumentTypes, sendTo } =
                config;
            const { data: documents } = thunkApi.getState().projectDocuments.info;
            const { selectedTemplate, typeOfDialog } =
                thunkApi.getState().projectDocumentsReportFromTemplate;

            const reportConfig = {
                ...selectedTemplate,
                DocumentChoice: selectedVersionToInclude,
                DocumentChoice_DocType: selectedDocumentTypes,
                EMailRecipients: sendTo,
                SendByEMail: sendTo.length > 0 ? '1' : '0',
            };

            const universalIds =
                typeOfDialog === CreateReportFromTemplateDialogEnum.SelectedDocuments
                    ? documents.map(item => item.universalId).join('|')
                    : '';

            const formData = new FormData();
            formData.append('universalIds', universalIds);
            formData.append('reportConfig', JSON.stringify(reportConfig));

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlReportsExcelReport, request);

            if (response.ok) {
                if (sendTo.length < 1) {
                    downloadDocumentsStream(await response.blob(), fileName);
                }

                return Promise.resolve(
                    typeOfDialog === CreateReportFromTemplateDialogEnum.SelectedDocuments
                        ? documents.length
                        : 0,
                );
            }

            return Promise.reject(new Error(`${fileName}.zip`));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    downloadRedliningReport: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<void> => {
        const { replicaId, universalId } = config;
        const fileName = 'Redlining Report.pdf';

        try {
            const formData = new FormData();
            formData.append('universalId', universalId);

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlReportsRedliningReport, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            downloadDocumentsStream(await response.blob(), fileName);
            return Promise.resolve();
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsReportFromTemplateState>) => {
        /**
         * Reducers for the 'getReportTemplatesForCreateReport' thunk.
         */
        builder.addCase(
            getReportTemplatesForCreateReport.pending,
            (state: Draft<IProjectDocumentsReportFromTemplateState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getReportTemplatesForCreateReport.fulfilled,
            (
                state: Draft<IProjectDocumentsReportFromTemplateState>,
                action: IAction<IGetReportTemplatesResponse>,
            ) => {
                state.templates = standardizeAndSortTemplates(
                    action.payload.templates,
                    action.payload.DocTypes,
                );
                state.selectedTemplate = state.templates[0];
                state.docTypes = action.payload.DocTypes;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            getReportTemplatesForCreateReport.rejected,
            (
                state: Draft<IProjectDocumentsReportFromTemplateState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'onConfirmCreateReport' thunk.
         */
        builder.addCase(
            onConfirmCreateReport.pending,
            (state: Draft<IProjectDocumentsReportFromTemplateState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            onConfirmCreateReport.fulfilled,
            (state: Draft<IProjectDocumentsReportFromTemplateState>, action: IAction<number>) => {
                state.isReportCreated = true;
                state.downloadedReports = action.payload;

                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            onConfirmCreateReport.rejected,
            (
                state: Draft<IProjectDocumentsReportFromTemplateState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'downloadRedliningReport' thunk.
         */
        builder.addCase(
            downloadRedliningReport.pending,
            (state: Draft<IProjectDocumentsReportFromTemplateState>) => {
                state.redliningReportState = 'Pending';
            },
        );
        builder.addCase(
            downloadRedliningReport.fulfilled,
            (state: Draft<IProjectDocumentsReportFromTemplateState>) => {
                state.redliningReportState = 'Succeed';
            },
        );
        builder.addCase(
            downloadRedliningReport.rejected,
            (
                state: Draft<IProjectDocumentsReportFromTemplateState>,
                action: IAction<SerializedError>,
            ) => {
                state.redliningReportState =
                    action.error.message === 'ERROR_NO_REDLININGS' ? 'NoRedlinings' : 'Failed';
            },
        );
    },
};

export const onConfirmCreateReport = createAsyncThunk(
    urlReportsExcelReport,
    thunks.onConfirmCreateReport,
);

export const getReportTemplatesForCreateReport = createAsyncThunk(
    urlReportsExcelTemplates,
    thunks.getReportTemplatesForCreateReport,
);

export const downloadRedliningReport = createAsyncThunk(
    urlReportsRedliningReport,
    thunks.downloadRedliningReport,
);

export enum SelectReportVersionEnum {
    SelectedDocuments = '1',
    DocumentsCurrentIndex = '2',
    DocumentsAllIndexes = '3',
    DocumentsFormula = '4',
    SelectedDocumentsAllIndexes = '6',
}

export enum CreateReportFromTemplateDialogEnum {
    Database = 'database',
    SelectedDocuments = 'selected',
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocumentsReportFromTemplateState extends SliceProps {
    isDialogOpen: boolean;
    isReportCreated: boolean;
    selectedTemplate: IReportTemplate;
    templates: IReportTemplate[];
    docTypes: string[];
    downloadedReports: number;
    redliningReportState: RedliningReportState;
    typeOfDialog: CreateReportFromTemplateDialogEnum;
}

type RedliningReportState = LoadingState | 'NoRedlinings';

interface IGetReportTemplatesResponse {
    DocTypes: string[];
    templates: IReportTemplate[];
}

/**
 * Create the redux slice.
 */
export const projectDocumentsReportFromTemplateSlice = createSlice({
    name: 'projectDocumentsReportFromTemplate',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const getReportTemplates = (state: IAppReduxStore) => {
    return [state.projectDocumentsShareEmail.templates || []];
};
/**
 * Export functions from slice actions.
 */
export const {
    openCreateReportFromTemplateDialog,
    closeCreateReportFromTemplateDialog,
    selectReportTemplate,
    resetAfterDownloadingReports,
    resetDownloadedReportsErrorState,
    resetRedliningReportState,
} = projectDocumentsReportFromTemplateSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentsReportFromTemplateSlice.reducer;
